import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkle, LockKey, User as UserIcon, Package, MagnifyingGlass } from "@phosphor-icons/react";
import InstallAppButton from "@/components/InstallAppButton";

const BG = "https://images.pexels.com/photos/29988985/pexels-photo-29988985.jpeg";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("staff"); // staff | customer
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [customerMobile, setCustomerMobile] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email, password);
      const dest = (u.role === "admin" || u.role === "secondary_admin" || u.role === "accountant")
        ? "/admin"
        : u.role === "fabrication" ? "/fabrication"
        : u.role === "delivery" ? "/delivery"
        : "/operator";
      nav(dest, { replace: true });
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  const submitCustomer = (e) => {
    e.preventDefault();
    setErr("");
    const digits = customerMobile.replace(/\D/g, "");
    if (digits.length < 10) {
      setErr("Please enter a valid 10-digit mobile number");
      return;
    }
    nav(`/my-orders/${digits.slice(-10)}`);
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2">
      {/* Left visual */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src={BG}
          alt="Laser cutter sparks"
          className="absolute inset-0 h-full w-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900 via-slate-900/70 to-transparent" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="Shobhit Creation"
              className="h-12 w-auto object-contain drop-shadow-lg"
              data-testid="brand-logo-hero"
            />
          </div>
          <div className="space-y-4 max-w-md">
            <div className="text-xs font-mono-num text-orange-400 tracking-widest">
              // FACTORY OS · v1.0
            </div>
            <h1 className="font-heading text-5xl font-black leading-tight text-white">
              Precision cut.<br />Every sheet, every order.
            </h1>
            <p className="text-slate-300 text-sm max-w-sm">
              Order intake, sqft calculation, cutting queue and account tallies —
              purpose-built for the CNC laser workshop.
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bp-grid">
        <div className="w-full max-w-md space-y-4">
          <div className="lg:hidden flex items-center gap-3 mb-2">
            <img
              src="/logo.png"
              alt="Shobhit Creation"
              className="h-10 w-auto object-contain"
              data-testid="brand-logo"
            />
          </div>

          {/* Mode toggle */}
          <div className="flex items-center gap-1 surface-2 p-1 rounded-sm" data-testid="login-mode-toggle">
            <button
              type="button"
              onClick={() => { setMode("staff"); setErr(""); }}
              className={
                "flex-1 px-3 py-2 rounded-sm text-xs font-mono-num tracking-widest transition-colors " +
                (mode === "staff" ? "bg-orange-500 text-white" : "text-slate-400 hover:text-slate-200")
              }
              data-testid="login-mode-staff"
            >
              <UserIcon size={12} className="inline mr-1.5" weight="bold"/> STAFF LOGIN
            </button>
            <button
              type="button"
              onClick={() => { setMode("customer"); setErr(""); }}
              className={
                "flex-1 px-3 py-2 rounded-sm text-xs font-mono-num tracking-widest transition-colors " +
                (mode === "customer" ? "bg-orange-500 text-white" : "text-slate-400 hover:text-slate-200")
              }
              data-testid="login-mode-customer"
            >
              <Package size={12} className="inline mr-1.5" weight="bold"/> TRACK YOUR ORDER
            </button>
          </div>

          {mode === "staff" ? (
            <form
              onSubmit={submit}
              className="surface p-8 space-y-6"
              data-testid="login-form"
            >
              <div>
                <div className="text-xs font-mono-num text-slate-400 tracking-widest mb-2">
                  // AUTHENTICATE
                </div>
                <h2 className="font-heading text-3xl font-black text-white">Sign in</h2>
                <p className="text-sm text-slate-400 mt-1">
                  Admin & operator access · workshop terminal
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300 font-mono-num text-xs tracking-widest">
                    EMAIL / MOBILE
                  </Label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                    <Input
                      id="email"
                      data-testid="login-email-input"
                      type="text"
                      autoComplete="username"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@shobhit.com or 9911223344"
                      className="pl-10 bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm focus-visible:ring-orange-500"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-300 font-mono-num text-xs tracking-widest">
                    PASSWORD
                  </Label>
                  <div className="relative">
                    <LockKey className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                    <Input
                      id="password"
                      data-testid="login-password-input"
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm focus-visible:ring-orange-500"
                      required
                    />
                  </div>
                </div>
              </div>

              {err && (
                <div
                  data-testid="login-error"
                  className="text-sm text-red-400 font-mono-num bg-red-500/10 border border-red-500/40 px-3 py-2 rounded-sm"
                >
                  {err}
                </div>
              )}

              <Button
                type="submit"
                data-testid="login-submit-button"
                disabled={busy}
                className="w-full h-12 btn-industrial rounded-sm text-base"
              >
                <Sparkle weight="fill" size={18} className="mr-2" />
                {busy ? "AUTHENTICATING…" : "ENTER WORKSHOP"}
              </Button>

              <div className="pt-2 flex justify-center">
                <InstallAppButton />
              </div>
            </form>
          ) : (
            <form
              onSubmit={submitCustomer}
              className="surface p-8 space-y-6"
              data-testid="customer-track-form"
            >
              <div>
                <div className="text-xs font-mono-num text-emerald-400 tracking-widest mb-2">
                  // TRACK YOUR ORDER
                </div>
                <h2 className="font-heading text-3xl font-black text-white">Namaste 🙏</h2>
                <p className="text-sm text-slate-400 mt-1">
                  Apna mobile number daaliye — aapke saare orders ka status, progress aur
                  balance ek jagah dikh jaayenge. <b className="text-emerald-400">Koi password nahi chahiye.</b>
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="customer-mobile" className="text-slate-300 font-mono-num text-xs tracking-widest">
                  MOBILE NUMBER
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-mono-num text-sm">+91</span>
                  <Input
                    id="customer-mobile"
                    data-testid="customer-mobile-input"
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="tel"
                    maxLength={10}
                    value={customerMobile}
                    onChange={(e) => setCustomerMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="98xxxxxxxx"
                    className="pl-12 bg-slate-800/60 border-slate-600 text-white h-12 rounded-sm focus-visible:ring-orange-500 font-mono-num text-lg tracking-wider"
                    required
                  />
                </div>
                <div className="text-[11px] font-mono-num text-slate-500">
                  Jis number pe order book kiya tha, wahi daaliye.
                </div>
              </div>

              {err && (
                <div
                  data-testid="customer-track-error"
                  className="text-sm text-red-400 font-mono-num bg-red-500/10 border border-red-500/40 px-3 py-2 rounded-sm"
                >
                  {err}
                </div>
              )}

              <Button
                type="submit"
                data-testid="customer-track-submit"
                disabled={customerMobile.length < 10}
                className="w-full h-12 rounded-sm text-base bg-emerald-600 hover:bg-emerald-500 text-white font-heading tracking-widest disabled:opacity-40"
              >
                <MagnifyingGlass weight="bold" size={18} className="mr-2" />
                VIEW MY ORDERS
              </Button>

              <div className="text-[11px] text-slate-500 font-mono-num border-t border-slate-700 pt-4">
                Order number hai? Direct <a href="#" onClick={(e) => e.preventDefault()} className="text-orange-400">/track/&lt;order-no&gt;</a> link se bhi check kar sakte hain.
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
